import { useQuery } from '@apollo/client'
import { GlobalSearchQuery } from '../globalSearch.gql'
import { SearchResults } from '../types'

interface UseGlobalSearchResult {
    results: SearchResults
    isLoading: boolean
    error: Error | null
    retry: () => void
}

const EMPTY_RESULTS: SearchResults = {
    candidates: [],
    rosters: [],
    companies: [],
}

export function useGlobalSearch(query: string): UseGlobalSearchResult {
    const { data, loading, error, refetch } = useQuery(GlobalSearchQuery, {
        variables: { query },
        skip: !query,
        fetchPolicy: 'network-only',
    })

    const retry = () => {
        if (query) {
            refetch({ query })
        }
    }

    if (!query || !data?.globalSearch) {
        return {
            results: EMPTY_RESULTS,
            isLoading: loading,
            error: error ?? null,
            retry,
        }
    }

    const results: SearchResults = {
        candidates: data.globalSearch.candidates.map((c) => ({
            id: c.id,
            type: 'candidate' as const,
            name: c.name,
            subtitle: c.subtitle ?? undefined,
            path: `/people/${c.id}/overview/on`,
        })),
        rosters: data.globalSearch.rosters.map((r) => ({
            id: r.id,
            type: 'roster' as const,
            name: r.name,
            subtitle: r.subtitle ?? undefined,
            path: `/searches/${r.id}/candidates/on`,
        })),
        companies: data.globalSearch.companies.map((c) => ({
            id: c.id,
            type: 'company' as const,
            name: c.name,
            subtitle: c.subtitle ?? undefined,
            path: `/companies/${c.id}`,
        })),
    }

    return {
        results,
        isLoading: loading,
        error: error ?? null,
        retry,
    }
}
